import { useEffect, useState } from 'react';

// Harbor is a single page: a tab left open across a deploy keeps running the
// old code against the new server until someone refreshes. Each build gives
// the main script a new name (main.<hash>.js), and index.html is served
// uncached, so comparing the script this tab loaded with the one the current
// index.html points to tells us a new version is out.
//
// Checked every few minutes and whenever the tab comes back into view.
// Returns true once a newer build is live; the page then offers a refresh
// rather than reloading on its own, so nothing half-typed is lost.

const CHECK_EVERY_MS = 5 * 60 * 1000;
const MAIN_SCRIPT = /\/static\/js\/main\.[\w-]+\.js/;

function loadedMainScript() {
  const el = Array.from(document.scripts).find((s) => MAIN_SCRIPT.test(s.src || ''));
  const m = el && el.src.match(MAIN_SCRIPT);
  return m ? m[0] : null;
}

export default function useNewVersion() {
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    const mine = loadedMainScript();
    // Development server (no hashed bundle): nothing to compare.
    if (!mine) return undefined;

    let stopped = false;
    const check = async () => {
      if (stopped || document.visibilityState !== 'visible') return;
      try {
        const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
        if (!res.ok) return;
        const m = (await res.text()).match(MAIN_SCRIPT);
        if (m && m[0] !== mine) {
          setUpdated(true);
          stopped = true;
        }
      } catch {
        // Offline or mid-deploy: try again next time.
      }
    };

    const timer = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return updated;
}
